import React, { useState } from 'react';
import { useMutation } from '@/apollo';
import { Trash2, AlertTriangle, Calendar, Database } from 'lucide-react';
import { CLEAR_OLD_LOGS } from '../../graphql/logs';
import { useNotification } from '../ui/Notification';

interface LogRetentionSettingsProps {}

type LogCategory = 
  | 'USER_ACTION'
  | 'AUTHENTICATION'
  | 'LOCATION'
  | 'CONDITION'
  | 'PROVIDER'
  | 'INSURANCE'
  | 'APPOINTMENT_DETAIL'
  | 'BULLETIN'
  | 'SYSTEM'
  | null;

const LOG_CATEGORIES: Array<{ value: LogCategory; label: string }> = [
  { value: null, label: 'All Categories' },
  { value: 'USER_ACTION', label: 'User Actions' },
  { value: 'AUTHENTICATION', label: 'Authentication' },
  { value: 'LOCATION', label: 'Locations' },
  { value: 'CONDITION', label: 'Conditions' },
  { value: 'PROVIDER', label: 'Providers' },
  { value: 'INSURANCE', label: 'Insurances' },
  { value: 'APPOINTMENT_DETAIL', label: 'Appointment Details' },
  { value: 'BULLETIN', label: 'Bulletins' },
  { value: 'SYSTEM', label: 'System Logs' },
];

const PRESET_RETENTION_DAYS = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 60, label: '60 days' },
  { days: 90, label: '90 days' },
  { days: 180, label: '180 days' },
  { days: 365, label: '1 year' },
];

export const LogRetentionSettings: React.FC<LogRetentionSettingsProps> = () => {
  const { showNotification } = useNotification();
  const [olderThanDays, setOlderThanDays] = useState<number>(90);
  const [category, setCategory] = useState<LogCategory>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const [clearOldLogs] = useMutation(CLEAR_OLD_LOGS, {
    onCompleted: () => {
      setIsClearing(false);
      setShowConfirmModal(false);
      showNotification('success', 'Logs Cleared', 'Old logs have been successfully deleted.');
    },
    onError: (error) => {
      setIsClearing(false);
      setShowConfirmModal(false);
      showNotification('error', 'Clear Failed', `Failed to clear logs: ${error.message}`);
    },
  });

  const handleClearLogs = async () => {
    setIsClearing(true);
    try {
      await clearOldLogs({
        variables: {
          olderThanDays,
          category: category || undefined,
        },
      });
    } catch (error) {
      // Error handled in onError callback
    }
  };

  const getCutoffDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - olderThanDays);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold flex items-center" style={{ color: 'var(--color-text-primary)' }}>
            <Database className="w-6 h-6 mr-2" style={{ color: 'var(--color-accent)' }} />
            Log Retention & Cleanup
          </h3>
          <p className="mt-1 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            Manage log retention policies and clean up old log entries
          </p>
        </div>
      </div>

      {/* Warning Banner */}
      <div
        className="rounded-lg p-4 border flex items-start space-x-3"
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          borderColor: 'var(--color-warning)',
          borderWidth: '1px',
        }}
      >
        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-warning)' }} />
        <div className="flex-1">
          <h4 className="font-semibold mb-1" style={{ color: 'var(--color-warning)' }}>
            Important Notice
          </h4>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            This action will permanently delete logs older than the specified number of days. This cannot be undone.
            Make sure you have exported any logs you need before proceeding.
          </p>
        </div>
      </div>

      {/* Configuration Card */}
      <div
        className="rounded-xl shadow-sm border p-6"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
        }}
      >
        <div className="space-y-6">
          {/* Retention Period */}
          <div>
            <label className="block text-sm font-medium mb-3" style={{ color: 'var(--color-text-primary)' }}>
              <Calendar className="w-4 h-4 inline mr-2" />
              Retention Period
            </label>
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {PRESET_RETENTION_DAYS.map((preset) => (
                  <button
                    key={preset.days}
                    onClick={() => setOlderThanDays(preset.days)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      olderThanDays === preset.days
                        ? 'shadow-sm'
                        : ''
                    }`}
                    style={{
                      backgroundColor:
                        olderThanDays === preset.days
                          ? 'var(--color-accent)'
                          : 'var(--color-bg-secondary)',
                      color:
                        olderThanDays === preset.days
                          ? 'white'
                          : 'var(--color-text-primary)',
                      border: olderThanDays === preset.days
                        ? 'none'
                        : '1px solid var(--color-border)',
                    }}
                    onMouseEnter={(e) => {
                      if (olderThanDays !== preset.days) {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (olderThanDays !== preset.days) {
                        e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                      }
                    }}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center space-x-4">
                <label className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  Custom:
                </label>
                <input
                  type="number"
                  min="1"
                  max="3650"
                  value={olderThanDays}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    if (!isNaN(value) && value > 0) {
                      setOlderThanDays(Math.min(value, 3650));
                    }
                  }}
                  className="px-3 py-2 border rounded-lg w-24 text-sm no-spinners"
                  style={{
                    backgroundColor: 'var(--color-bg-primary)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)',
                  }}
                />
                <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                  days
                </span>
              </div>
              <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
                Logs older than <strong style={{ color: 'var(--color-text-primary)' }}>{getCutoffDate()}</strong> will be deleted
              </p>
            </div>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium mb-3" style={{ color: 'var(--color-text-primary)' }}>
              Category Filter (Optional)
            </label>
            <select
              value={category || ''}
              onChange={(e) => setCategory(e.target.value as LogCategory || null)}
              className="w-full px-3 py-2 border rounded-lg text-sm"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-primary)',
              }}
            >
              {LOG_CATEGORIES.map((cat) => (
                <option key={cat.value || 'all'} value={cat.value || ''}>
                  {cat.label}
                </option>
              ))}
            </select>
            <p className="text-xs mt-2" style={{ color: 'var(--color-text-muted)' }}>
              {category
                ? `Only ${LOG_CATEGORIES.find((c) => c.value === category)?.label.toLowerCase()} logs will be deleted`
                : 'All log types will be deleted'}
            </p>
          </div>

          {/* Action Button */}
          <div className="flex justify-end pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
            <button
              onClick={() => setShowConfirmModal(true)}
              disabled={isClearing}
              className="px-6 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: 'var(--color-error)',
                color: 'white',
              }}
              onMouseEnter={(e) => {
                if (!isClearing) {
                  e.currentTarget.style.opacity = '0.9';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              <Trash2 className="w-4 h-4" />
              <span>Clear Old Logs</span>
            </button>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => !isClearing && setShowConfirmModal(false)}
        >
          <div
            className="rounded-2xl shadow-2xl max-w-md w-full border relative"
            style={{
              backgroundColor: 'var(--color-bg-primary)',
              borderColor: 'var(--color-border)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-start space-x-3 mb-4">
                <AlertTriangle className="w-6 h-6 flex-shrink-0" style={{ color: 'var(--color-error)' }} />
                <div>
                  <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>
                    Confirm Log Deletion
                  </h3>
                  <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
                    You are about to permanently delete all logs older than <strong>{olderThanDays} days</strong>
                    {category && (
                      <>
                        {' '}in the <strong>{LOG_CATEGORIES.find((c) => c.value === category)?.label}</strong> category
                      </>
                    )}
                    .
                  </p>
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
                    <p className="text-sm font-medium text-red-800 dark:text-red-200">
                      ⚠️ This action cannot be undone. All matching logs will be permanently deleted.
                    </p>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    Cutoff date: <strong>{getCutoffDate()}</strong>
                  </p>
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowConfirmModal(false)}
                  disabled={isClearing}
                  className="px-6 py-2 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    color: 'var(--color-text-primary)',
                    border: '1px solid var(--color-border)',
                  }}
                  onMouseEnter={(e) => {
                    if (!isClearing) {
                      e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleClearLogs}
                  disabled={isClearing}
                  className="px-6 py-2 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  style={{
                    backgroundColor: 'var(--color-error)',
                    color: 'white',
                  }}
                  onMouseEnter={(e) => {
                    if (!isClearing) {
                      e.currentTarget.style.opacity = '0.9';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = '1';
                  }}
                >
                  {isClearing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      <span>Deleting...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      <span>Delete Logs</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

